import { Table, message } from "antd"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { FiEdit2, FiTrash } from "react-icons/fi"
import styled from "styled-components"

import {
    createEventType,
    deleteEventType,
    updateEventType,
} from "../../../../../api"
import StyledAppButton from "../../../../_shared/components/AppButton"
import FloatingButton from "../../../../_shared/components/FloatingButton"
import Popconfirm from "../../../../_shared/components/Popconfirm"
import StyledScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"
import { useStateValue } from "../../../../_shared/context/AppStateStore"
import useRestTable from "../../../../_shared/hooks/usePaginatedRestResource"
import EventTypesBottomSheet from "./EventTypesBottomSheet"

// ----------------------------------------------------------------------------

function _EventTypes(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [eventTypes, fetchEventTypes, isLoading, pagination, total] =
        useRestTable("/event-types", token)

    const [selected, setSelected] = useState(null)

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    async function save(values) {
        try {
            if (values.id) {
                await updateEventType(values.id, values, token)
            } else {
                await createEventType(values, token)
            }
            fetchEventTypes()
            message.success("Operazione completata")
            closeSheet()
        } catch (e) {
            message.error(`Non siamo riusciti a creare l'evento: ${e}`)
        }
    }

    function closeSheet() {
        setSelected(null)
    }

    function openEventForm(event) {
        return () => setSelected({ ...event })
    }

    async function handleDeleteEvent(row) {
        try {
            await deleteEventType(row.id, token)
            message.success("Evento eliminato")
            fetchEventTypes()
        } catch (err) {
            // eslint-disable-next-line
            console.log(err)
            message.error("Qualcosa è andato storto")
        }
    }

    function renderRowActions(row) {
        return (
            <div className="row-actions">
                <FloatingButton onClick={openEventForm(row)}>
                    <FiEdit2 className="icn" />
                </FloatingButton>
                <Popconfirm
                    placement="left"
                    title="Sei sicuro di voler eliminare questo catalogo?"
                    okText="Sì"
                    cancelText="No"
                    onConfirm={() => {
                        handleDeleteEvent(row)
                    }}
                >
                    <FloatingButton>
                        <FiTrash className="icn" />
                    </FloatingButton>
                </Popconfirm>
            </div>
        )
    }

    function handleTableChange(pagination) {
        fetchEventTypes({ pagination })
    }

    function getPaginationObject() {
        return {
            size: "small",
            total: total,
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: false,
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <StyledScreenTitle text="Tipologie di evento"></StyledScreenTitle>
            <div className="top-bar">
                <StyledAppButton
                    onClick={openEventForm({})}
                    uxType="primary"
                    htmlType="button"
                >
                    Crea nuova tipologia
                </StyledAppButton>
            </div>
            <div className="container">
                <Table
                    dataSource={eventTypes}
                    rowKey={(row) => row.id}
                    columns={getColumns(renderRowActions)}
                    loading={isLoading}
                    pagination={getPaginationObject()}
                    onChange={handleTableChange}
                    checkStrictly={true}
                />
            </div>
            <EventTypesBottomSheet
                open={selected ? true : false}
                document={selected}
                onConfirm={save}
                onCancel={closeSheet}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_EventTypes.propTypes = {
    className: PropTypes.string.isRequired,
}

_EventTypes.defaultProps = {}

// ----------------------------------------------------------------------------

const EventTypes = styled(_EventTypes)`
    & {
        h1 {
            text-align: left;
        }
        .top-bar {
            margin: 20px 0;

            display: flex;
            flex-direction: row;
            align-items: center;
        }

        .ant-table-cell {
            .row-actions {
                display: flex;
                justify-content: space-between;
            }
        }

        .table {
            tr {
                .row-actions {
                    display: flex;
                    justify-content: space-between;
                    transition: all 0.2s ease-in-out;
                    opacity: 0;
                }

                :hover {
                    .row-actions {
                        opacity: 1;
                    }
                }
            }
        }

        .icn {
            font-size: 20px;
            color: ${(props) => props?.theme?.colors?.textPrimary};
        }
    }
`
// ----------------------------------------------------------------------------

export default EventTypes

export function getColumns(renderRowActions) {
    return [
        { title: "Nome", dataIndex: "name", key: "name" },
        { title: "Età minima", dataIndex: "minAge", key: "minAge" },
        { title: "Età massima", dataIndex: "maxAge", key: "maxAge" },
        {
            title: "Durata visita",
            dataIndex: "slotDuration",
            key: "slotDuration",
        },
        {
            key: "actions",
            render: renderRowActions,
        },
    ]
}
